import { useRef, useState } from "react";
import { Skeleton } from "@/components/ui/skeleton";
import { useNavigate } from "react-router-dom";
import { useMeetingIdForActivity } from "@/hooks/use-meeting-summary";
import { useFollowUpFlow } from "@/hooks/use-follow-up-flow";
import { useNextFollowUpDue } from "@/hooks/use-follow-ups";
import { useOppdagQueue } from "@/hooks/use-oppdag-queue";
import { usePermissions } from "@/hooks/use-permissions";
import { useQueueHotkeys } from "@/hooks/use-queue-hotkeys";
import { useSignalCardFlow } from "@/hooks/use-signal-card-flow";
import { useOppdagKpis } from "@/hooks/use-oppdag-kpis";
import { useWeekProgress } from "@/hooks/use-week-progress";
import { meetingFollowUpLabel, primaryActionFor } from "@/lib/follow-up-calc";
import FollowUpFlowDialogs from "./FollowUpFlowDialogs";
import FollowUpSideList from "./FollowUpSideList";
import NewFollowUpDialog from "./NewFollowUpDialog";
import QueueCardStack from "./QueueCardStack";
import QueueEmptyState from "./QueueEmptyState";
import OppdagKpiRow from "./OppdagKpiRow";

/** Oppdag: KPI-ene dine, ett kort om gangen, og ukens oppfølginger ved siden av. */
export default function OppdagTab() {
  const [focusId, setFocusId] = useState<string | null>(null);
  const [newOpen, setNewOpen] = useState(false);
  const { isAdmin } = usePermissions();
  const queue = useOppdagQueue({ focusId });
  const progress = useWeekProgress();
  const kpis = useOppdagKpis();
  const { data: nextDue } = useNextFollowUpDue();
  const flow = useFollowUpFlow();

  const followUp = queue.current?.kind === "follow_up" ? queue.current.followUp : null;
  const signal = queue.current?.kind === "signal" ? queue.current.signal : null;
  const signalFlow = useSignalCardFlow(signal);
  const navigate = useNavigate();
  const { data: summaryMeetingId } = useMeetingIdForActivity(followUp && meetingFollowUpLabel(followUp) !== null ? followUp.source_activity_id : null);
  const dismissSignalRef = useRef<(() => void) | null>(null);
  const dismissFollowUpRef = useRef<(() => void) | null>(null);
  /** E-post, Ring og Følg opp senere på et signal: ta det, og gå rett videre på oppfølgingen som ble laget. */
  const claimThen = async (action: "email" | "phone" | "postpone") => {
    const created = await signalFlow.claim();
    if (created) flow.start(action, created);
  };
  const followUpAction = (action: "email" | "phone" | "postpone") => {
    if (followUp && (action === "postpone" || meetingFollowUpLabel(followUp) === null)) flow.start(action, followUp);
  };
  const hotkeysOn = (!!followUp || (!!signal && !signalFlow.pending)) && flow.step.kind === "idle" && !newOpen;
  useQueueHotkeys(
    {
      onPrimary: followUp
        ? () => {
            if (meetingFollowUpLabel(followUp) !== null) {
              if (summaryMeetingId) navigate(`/kunde/${followUp.customer_id}/moter/${summaryMeetingId}`);
              return;
            }
            flow.start(primaryActionFor(followUp), followUp);
          }
        : undefined,
      onSecondary: () => (signal ? dismissSignalRef.current?.() : dismissFollowUpRef.current?.()),
      onEmail: () => (signal ? void claimThen("email") : followUpAction("email")),
      onPhone: () => (signal ? void claimThen("phone") : followUpAction("phone")),
      onPostpone: () => (signal ? void claimThen("postpone") : followUpAction("postpone")),
      onClose: () => dismissFollowUpRef.current?.(),
    },
    hotkeysOn,
  );

  return (
    <div className="space-y-6">
      <OppdagKpiRow
        contactsToday={kpis.contacts.today}
        contactsWeek={kpis.contacts.week}
        meetingsWeek={kpis.meetingsThisWeek}
        followUpsLeft={progress.remaining}
      />

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2">
          {queue.isLoading ? (
            <Skeleton className="h-72 w-full" />
          ) : queue.current ? (
            <QueueCardStack
              card={queue.current}
              behind={(followUp ? queue.remainingFollowUps : queue.signalsAvailable) - 1}
              onAction={(action) => followUp && flow.start(action, followUp)}
              followUpDismissRef={dismissFollowUpRef}
              followUpPosition={
                followUp ? { position: queue.openFollowUps.findIndex((f) => f.id === followUp.id) + 1, total: queue.openFollowUps.length } : undefined
              }
              signalActions={{
                position: 1,
                total: signal?.queue_total ?? queue.signalsAvailable,
                isAdmin,
                pending: signalFlow.pending,
                processId: signalFlow.processId,
                onProcessChange: signalFlow.setProcessId,
                onEmail: () => void claimThen("email"),
                onPhone: () => void claimThen("phone"),
                onLater: () => void claimThen("postpone"),
                onDismiss: () => void signalFlow.dismiss(),
                onArchiveAll: signalFlow.archiveAll,
                dismissRef: dismissSignalRef,
              }}
            />
          ) : (
            <QueueEmptyState nextDue={nextDue} onNewFollowUp={() => setNewOpen(true)} />
          )}
        </div>
        <FollowUpSideList
          open={queue.openFollowUps}
          done={queue.doneFollowUps}
          currentId={followUp?.id ?? null}
          onSelect={setFocusId}
        />
      </div>

      <FollowUpFlowDialogs flow={flow} />
      <NewFollowUpDialog open={newOpen} onOpenChange={setNewOpen} />
    </div>
  );
}
